/**
 * **攻撃の矢印の形**（Issue #552。攻撃宣言 `effects/attack.js` と、盤に残る印 `drawObjects.js::attackArrow` が共有する）。
 *
 * 矢印は**攻撃宣言の帯の中で伸び**（three の板）、伸び切ったら**盤に置かれた印**（2D canvas）が引き継いで
 * バトルが終わるまで残る。継ぎ目で形が変わらないよう、**形は 1 か所（ここ）で決め**、
 * 描く側は「画像の部品」の並びをそのまま写すだけにする。
 *
 * ## 画像（Gemini で生成して緑を抜き、3 つに切ったもの。ユーザー 2026-09-26「矢印がダサい → 画像で」）
 *
 * 長さが攻撃ごとに変わるので **柄（`attackArrowShaft.png`）・矢じり（`attackArrowHead.png`）** に分けてある
 * （尾も作ったが、盤のカードのイラストを隠すので使わない。ユーザー 2026-09-26）。
 * どれも高さ 331px で、**軸の中心線が同じ高さ**（y ≒ 165）。柄は長さ方向に一様な模様なので横に引き伸ばす。
 * 柄は矢じりの**下に潜らせる**（描く順: 柄 → 矢じり）。攻撃する人の色は画像には入れない（赤の差し色のまま）。
 * 矢印は**攻撃するカードの縁から出て、対象のカードの縁で止まる**（盤のカードのイラストに掛けない）。
 * 隙間が矢じりより短ければ**矢じりだけ**を隙間に収まる大きさで出す。
 * 生成のプロンプトは THEME.md の頭 ＋「右向きの矢印を 尾 / 一様な柄 / 矢じり の 3 つに分けて緑の隙間を空けて並べる」。
 *
 * - 座標は**舞台のピクセル**（y 下向き）。ゾーンの 4 点は `zoneQuad(key)`（効果の `ctx.stage.field.zoneQuad` と
 *   印の `view.zoneQuad` は同じもの）から取る
 * - 数は**いつも同じ**（three の側は板を先に作っておき、毎フレーム位置だけ変えるため）
 *
 * three も canvas も import しない（純関数。canvas に塗る `paintPrims` だけ 2D の context を受け取る）。
 */
import shaftUrl from './attackArrowShaft.png';
import headUrl from './attackArrowHead.png';
import { imageReady, themeImage } from './images';

/** 部品の画像（URL）。three は `kit.urlTexture`、canvas は `themeImage` で読む */
export const ARROW_PARTS = Object.freeze({ shaft: shaftUrl, head: headUrl });

/**
 * 画像の寸法（px）。`join` は柄がつながる位置（尾は右から、矢じりは左から）、`shaftPx` は柄の太さ。
 */
const SRC = Object.freeze({
  h: 331,
  shaftPx: 68,
  head: { w: 351, join: 8 },     // 矢じりの x = 8 で柄が終わる
});

/** 直接攻撃の矢印の先（相手のモンスターの列から、列の間の何倍奥まで伸ばすか）と、画面の大きさ */
const DIRECT_REACH = 2.7;
const SCREEN = { w: 1920, h: 1080 };

/** 矢印の太さ（柄の太さ。舞台のピクセル）と、矢じりだけのときの長さの下限 */
export const ARROW = Object.freeze({ thick: 30, minHead: 22 });




/** ゾーンの 4 点 → 外接する箱 */
function boxOf(quad) {
  if (!Array.isArray(quad) || quad.length !== 4) return null;
  const xs = quad.map((p) => p[0]); const ys = quad.map((p) => p[1]);
  if (![...xs, ...ys].every(Number.isFinite)) return null;
  const x = Math.min(...xs); const y = Math.min(...ys);
  const w = Math.max(...xs) - x; const h = Math.max(...ys) - y;
  return { x, y, w, h, cx: x + w / 2, cy: y + h / 2 };
}

/** 箱の中心から向き (ux, uy) へ進んで箱の縁に当たるまでの長さ */
const edgeOf = (b, ux, uy) => Math.min(
  Math.abs(ux) > 1e-3 ? (b.w / 2) / Math.abs(ux) : Infinity,
  Math.abs(uy) > 1e-3 ? (b.h / 2) / Math.abs(uy) : Infinity,
);

/**
 * **矢印の端**（舞台のピクセル）。攻撃するカードの縁から出て、対象のカードの縁で止まる。
 * 直接攻撃は**相手の陣地の奥**（相手の魔法・罠の列よりさらに奥）を指す。照準は出さない（ユーザー 2026-09-26）。
 *
 * @param zoneQuad `(key) => 4 点 | null`
 * @param data `{ fromZone, toZone, direct, player }`
 * @returns `{ start, tip }`。写らなければ null
 */
export function arrowEnds(zoneQuad, { fromZone, toZone = null, direct = false, player = 0 } = {}) {
  const from = boxOf(zoneQuad?.(fromZone));
  if (!from) return null;
  let to;
  let box = null;
  if (!direct) {
    const b = boxOf(zoneQuad?.(toZone));
    if (!b) return null;
    to = [b.cx, b.cy];
    box = b;
  } else {
    // 相手のモンスターの列と魔法・罠の列の中心から「奥」の向きを出し、攻撃するカードの正面の列の奥へ
    const opp = player === 1 ? 0 : 1;
    const mons = [1, 2, 3, 4, 5].map((i) => boxOf(zoneQuad?.(`monster:${opp}:${i}`))).filter(Boolean);
    const mts = [1, 2, 3, 4, 5].map((i) => boxOf(zoneQuad?.(`magic_trap:${opp}:${i}`))).filter(Boolean);
    if (!mons.length || !mts.length) return null;
    const avg = (bs) => [bs.reduce((a, b) => a + b.cx, 0) / bs.length, bs.reduce((a, b) => a + b.cy, 0) / bs.length];
    const mc = avg(mons); const tc = avg(mts);
    const rx = tc[0] - mc[0]; const ry = tc[1] - mc[1];
    const rl = Math.max(1, Math.hypot(rx, ry));
    const r = [rx / rl, ry / rl];
    // 攻撃するカードから横にずれの一番少ない相手のモンスターの枠（＝正面の列）
    const lateral = (b) => {
      const dx = b.cx - from.cx; const dy = b.cy - from.cy;
      const along = dx * r[0] + dy * r[1];
      return Math.hypot(dx - r[0] * along, dy - r[1] * along);
    };
    const front = mons.reduce((best, b) => (lateral(b) < lateral(best) ? b : best), mons[0]);
    /*
     * 相手の魔法・罠の列を越えて**盤の外（相手のプレイヤーの側の余白）**まで。魔法・罠の列で止めると、そこに伏せたカードを
     * 攻撃しているように見えた（照準を消したので先の位置だけで直接攻撃と分かるようにする。ユーザー 2026-09-26）。画面の外には出さない
     */
    to = [front.cx + r[0] * rl * DIRECT_REACH, front.cy + r[1] * rl * DIRECT_REACH];
    to = [Math.max(24, Math.min(SCREEN.w - 24, to[0])), Math.max(24, Math.min(SCREEN.h - 24, to[1]))];
  }
  const dx = to[0] - from.cx; const dy = to[1] - from.cy;
  const d = Math.hypot(dx, dy);
  if (!(d > 1)) return null;
  const ux = dx / d; const uy = dy / d;
  const s0 = Math.min(edgeOf(from, ux, uy) + 4, d * 0.45);
  // 対象のカードの縁（直接攻撃は奥の点そのもの）
  const back = box ? Math.min(edgeOf(box, ux, uy) + 2, d * 0.45) : 0;
  const tip = [to[0] - ux * back, to[1] - uy * back];
  let start = [from.cx + ux * s0, from.cy + uy * s0];
  // 隙間がほとんど無ければ、矢じりの下限の長さぶんだけ手前から（攻撃するカードの縁に少し掛かる）
  if (Math.hypot(tip[0] - start[0], tip[1] - start[1]) < ARROW.minHead) start = [tip[0] - ux * ARROW.minHead, tip[1] - uy * ARROW.minHead];
  return { start, tip };
}

/**
 * **矢印の形**（画像の部品の並び。数はいつも同じ）。
 *
 * - `img`  … 部品 `part`（shaft / head）を中心 `(cx, cy)`・幅 `w`（向き `rot` の方向）・高さ `h` で置く
 * - `hidden: true` のものは描かない
 *
 * 隙間が矢じりより短ければ、柄を出さず**矢じりだけ**を隙間に収まる大きさで出す。
 *
 * @param ends `arrowEnds` の返り値
 * @param color 攻撃する人の色（今は使わない。矢印は画像）
 * @param grow 伸び（0〜1。1 で矢じりが対象のカードの縁）
 */
export function arrowPrims(ends, color, { grow = 1 } = {}) {
  const [x0, y0] = ends.start;
  const [x1, y1] = ends.tip;
  const full = Math.max(1, Math.hypot(x1 - x0, y1 - y0));
  const ux = (x1 - x0) / full; const uy = (y1 - y0) / full;
  const rot = Math.atan2(uy, ux);
  const len = full * Math.max(0, Math.min(1, grow));
  // 1 px あたりの倍率（柄の太さを ARROW.thick に）。隙間が矢じりより短ければ矢じりを隙間に収める
  const k0 = ARROW.thick / SRC.shaftPx;
  const k = Math.min(k0, len / SRC.head.w);
  const h = SRC.h * k;
  const headL = SRC.head.w * k;
  const at = (dist) => [x0 + ux * dist, y0 + uy * dist];
  const shaftTo = len - (SRC.head.w - SRC.head.join) * k;
  const hid = !(len > 4);
  return [
    { type: 'img', part: 'shaft', ...xy(at(shaftTo / 2)), w: Math.max(0.001, shaftTo), h, rot, hidden: hid || shaftTo < 2 },
    { type: 'img', part: 'head', ...xy(at(len - headL / 2)), w: headL, h, rot, hidden: hid },
  ];
}

const xy = ([cx, cy]) => ({ cx, cy });

/** 形の外接する箱（印の canvas の大きさ）。余白 `pad` */
export function primsBox(prims, pad = 8) {
  let x0 = Infinity; let y0 = Infinity; let x1 = -Infinity; let y1 = -Infinity;
  prims.forEach((p) => {
    if (p.hidden) return;
    const r = Math.hypot(p.w, p.h) / 2;
    x0 = Math.min(x0, p.cx - r); y0 = Math.min(y0, p.cy - r);
    x1 = Math.max(x1, p.cx + r); y1 = Math.max(y1, p.cy + r);
  });
  if (!Number.isFinite(x0)) return null;
  return { x: x0 - pad, y: y0 - pad, w: x1 - x0 + pad * 2, h: y1 - y0 + pad * 2 };
}

/** canvas に描く部品の画像（`paintPrims` に渡す）と、読み終わったか（描き直しの鍵） */
export const arrowImages = () => ({
  shaft: themeImage(ARROW_PARTS.shaft), head: themeImage(ARROW_PARTS.head),
});
export const arrowImagesReady = () => Object.values(arrowImages()).every(imageReady);

/** canvas に塗る（`ox, oy` は canvas の左上が舞台のどこか。`imgs` は部品の画像） */
export function paintPrims(ctx, prims, ox = 0, oy = 0, imgs = null) {
  prims.forEach((p) => {
    if (p.hidden) return;
    ctx.save();
    ctx.translate(p.cx - ox, p.cy - oy);
    ctx.rotate(p.rot);
    const im = imgs?.[p.part];
    if (imageReady(im)) ctx.drawImage?.(im, -p.w / 2, -p.h / 2, p.w, p.h);
    ctx.restore();
  });
}
